import { getCurrencySymbol } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'numericFormatPipe',
  standalone: true
})

/**
 * Pipe for formatting numeric values with
 * optional decimal places, currency symbol, and compact notation (K, M, B).
 */
export class NumericFormatPipe implements PipeTransform {

  /**
   * The locale used for number formatting.
   */
  protected locale: string;

  /**
   * Initializes the pipe with the user's browser locale or defaults to 'en-US'.
   */
  constructor() {
    this.locale = navigator.language || 'en-US';
  }

  /**
   * Transforms a numeric value into a formatted string with optional decimal places, currency, and compact notation.
   * - Values in the thousands, millions, or billions are formatted as K, M, or B respectively.
   * - Optionally prepends a currency symbol.
   * - Optionally scales the input value by a multiplier (e.g., 1000000 when source data is stored in millions).
   * - Returns null for undefined, null, NaN, or zero (unless showZero is true).
   *
   * @param value The numeric value to format.
   * @param decimalPlaces The number of decimal places to display (default is 2).
   * @param showZero Whether to show zero values (default is false).
   * @param currencyCode Optional currency code (e.g., 'USD', 'EUR') to prepend a symbol.
   * @param scale Optional multiplier applied to the value before formatting (default is 1). Use 1000000 if source values are stored in millions.
   * @returns The formatted string or null if the value is not displayable.
   */
  transform(
    value: number | undefined | null,
    decimalPlaces: number | null | undefined = 2,
    showZero: boolean = false,
    currencyCode: string | null | undefined = null,
    scale: number = 1
  ): string | null {

    if (value === undefined
      || value === null
      || Number.isNaN(value)
      || (value === 0 && showZero === false))
      return null;

    if (decimalPlaces === null || decimalPlaces === undefined)
      decimalPlaces = 2;

    // Apply scale multiplier (defaults to 1 — no change for existing callers)
    value = value * scale;

    const formatter = new Intl.NumberFormat(this.locale, {
      // Show no decimals if the number is an integer
      minimumFractionDigits: value % 1 === 0 ? 0 : decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });

    let symbol: string | null;
    let computedValue: number = value;
    let formattedValue: string | null = null;

    if (value >= 1000000000 || value <= -1000000000) {
      computedValue = value / 1000000000;
      formattedValue = formatter.format(computedValue) + 'B';
    } else if (value >= 1000000 || value <= -1000000) {
      computedValue = value / 1000000;
      formattedValue = formatter.format(computedValue) + 'M';
    } else if (value >= 1000 || value <= -1000) {
      computedValue = value / 1000;
      formattedValue = formatter.format(computedValue) + 'K';
    }
    else {
      formattedValue = formatter.format(computedValue);
    }

    if (currencyCode && currencyCode.length > 0) {
      symbol = getCurrencySymbol(currencyCode, 'narrow', this.locale);

      if (formattedValue && formattedValue.length > 0 && formattedValue.charAt(0) === '-')
        formattedValue = '-' + symbol + formattedValue.substring(1);
      else
        formattedValue = symbol + formattedValue;
    }

    return formattedValue;
  }

}

